import $ from 'jquery';
import { calculateRoundup, RoundupCalcInput } from './roundup-calculator';

/**
 * "Keep the Change" round-up: thin DOM binding over the pure calculateRoundup()
 * (roundup-calculator.ts). Reads the play-page inputs, recomputes on the relevant
 * events, and writes both baskets (in-page + sidebar). No-op when the round-up
 * markup isn't present.
 */
export default function initRoundup(): void {
    const $roundupYesRadio = $('#roundup');
    if (!$roundupYesRadio.length) {
        return;
    }

    // The round-up amount lives in .roundup-container; the Gift Aid question is a
    // sibling partial (.partial.gift-aid). Both slide together with the opt-in.
    const $roundupContainer = $('.roundup-container, .partial.gift-aid');
    const $roundupRadios = $('input[type=radio][name="roundup"]');
    const $roundupAmountInput = $('#roundup_amount');
    const $giftAidRadios = $('#add_gift_aid_yes, #add_gift_aid_no');
    const $numEntriesInputs = $('input[name="num_entries"]');
    const $perTicketPriceInput = $('#per_ticket_price');

    // Basket cells (present in both the in-page and sidebar baskets). Only the
    // in-page basket has a `.basket-container` wrapper to hide when no entries are
    // chosen; the sidebar basket has none and stays visible (RM parity).
    const $inPageBasket = $('.basket-container');
    const $numberOfTickets = $('.number-of-tickets');
    const $monthlyCost = $('.monthly-cost');
    const $donation = $('.donation');
    const $giftAidChoice = $('.giftaid-choice');
    const giftAidYesLabel = $('.giftaid-choice-label-yes').first().text().trim() || 'Yes';
    const giftAidNoLabel = $('.giftaid-choice-label-no').first().text().trim() || 'No';
    const $entryTotal = $('.entry-total');
    const $roundupRows = $('.donation-container, .entry-total-container');
    // Its own row: hidden until the Gift Aid question is answered, not just while
    // round-up is off — an unanswered question is not a "No".
    const $giftAidRow = $('.giftaid-choice-container');

    function readInput(): RoundupCalcInput {
        const checkedEntries = $numEntriesInputs.filter(':checked').val();
        const entries = checkedEntries
            ? parseInt(String(checkedEntries).replace(/\D/g, ''), 10)
            : 0;

        const roundupValues = [1, 2, 3, 4, 5].map((n) => {
            const raw = $('#round_up_amount_' + n).val();
            const value = parseFloat(String(raw));
            return Number.isFinite(value) ? value : null;
        });

        return {
            entries: Number.isFinite(entries) ? entries : 0,
            perTicketPrice: parseFloat(String($perTicketPriceInput.val())),
            roundupValues,
            roundupSelected: $roundupYesRadio.prop('checked') === true,
            enteredAmount: ($roundupAmountInput.val() as string) ?? '',
            giftAid: $('#add_gift_aid_yes').prop('checked') === true
                ? true
                : $('#add_gift_aid_no').prop('checked') === true
                    ? false
                    : null,
        };
    }

    function render(): void {
        const input = readInput();
        const result = calculateRoundup(input);

        // Always keep the (always-visible) sidebar cells accurate.
        $numberOfTickets.text(String(result.entries));
        $monthlyCost.text(result.monthlyCost);
        $donation.text(result.donation);
        $giftAidChoice.text(
            input.giftAid === null ? '' : input.giftAid ? giftAidYesLabel : giftAidNoLabel,
        );
        $entryTotal.text(result.total);

        $inPageBasket[result.showBasket ? 'show' : 'hide']();
        $roundupRows[result.showBasket && result.showRoundupRows ? 'show' : 'hide']();
        $giftAidRow[result.showBasket && result.showGiftAidRow ? 'show' : 'hide']();
    }

    // Overwrite the amount field with the suggestion for the current entries /
    // selection (blank when opting out or no value is configured — never "NaN"),
    // then recompute the basket from the field's new contents. Driven by user
    // interaction (entries change, opt-in toggle); initial load preserves any
    // session-restored value instead — see below.
    function suggestThenRender(): void {
        $roundupAmountInput.val(calculateRoundup(readInput()).suggestedRoundup);
        render();
    }

    // Gift Aid is only answerable while the supporter is opted in to round-up, so
    // the `required` attribute must track the container's visibility exactly: a
    // required radio inside the collapsed container is unfocusable and the browser
    // would silently refuse to submit the form. The server enforces this too (the
    // hidden sentinel in gift-aid.blade.php posts "" when unanswered), so this is
    // purely to spare the supporter a round-trip.
    function syncGiftAidRequired(required: boolean): void {
        if (required) {
            $giftAidRadios.attr('required', 'required');
        } else {
            $giftAidRadios.removeAttr('required');
        }
    }

    function toggleRoundupContainer(): void {
        if ($roundupYesRadio.prop('checked')) {
            $roundupContainer.slideDown();
            syncGiftAidRequired(true);
        } else {
            $roundupContainer.slideUp();
            $giftAidRadios.prop('checked', false);
            syncGiftAidRequired(false);
        }
        suggestThenRender();
    }

    $roundupRadios.on('change', toggleRoundupContainer);
    $numEntriesInputs.on('click', suggestThenRender);
    $giftAidRadios.on('click', render);
    $roundupAmountInput.on('blur', render);

    // Initial state: collapse the container unless already opted in, then paint.
    // Keep `required` in step with that, for a session-restored opt-in (the auto
    // opt-in on a fresh visit arrives later, via a change event).
    if (!$roundupYesRadio.prop('checked')) {
        $roundupContainer.hide();
        syncGiftAidRequired(false);
    } else {
        syncGiftAidRequired(true);
    }

    // Preserve an amount restored from the session when the supporter revisits
    // step 1 (the server renders it into the field); only offer a suggestion into
    // an empty field on a fresh load. User interactions still re-suggest normally.
    if (String($roundupAmountInput.val() ?? '').trim() === '') {
        suggestThenRender();
    } else {
        render();
    }
}
